import React, { useState, useCallback, useRef } from 'react';
import { hoyEnChile } from '../../utils/dateHelpers';
import { User, Users as UsersIcon, DollarSign } from 'lucide-react';
import { movimientoService } from '../../services/movimientoService';
import { ComboboxLazy } from '../ui/ComboboxLazy';
import { useMiembroSearchJA } from '../../hooks/useMiembroSearchJA';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { DialogShell } from '../ui/DialogShell';
import { ModalHeader } from '../ui/ModalHeader';
import { OwnerContext, buildOwnerPayload } from '../../types/actividad';
import { TextInput } from '../ui/TextInput';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { mensajeErrorDeNegocio } from '../../utils/erroresDeNegocio';
import { ComprobanteInput } from '../comprobantes/ComprobanteInput';
import { MedioPagoFolio } from '../comprobantes/MedioPagoFolio';
import { useComprobanteFormulario } from '../../hooks/useComprobanteFormulario';
import type { EntidadDinero } from '../../types/comprobante';

interface IngresoJAModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly ownerContext: OwnerContext;
}

function entidadDelIngreso(ownerContext: OwnerContext): EntidadDinero | null {
  return ownerContext.tipo === 'global' ? null : { ...ownerContext, tipo: ownerContext.tipo };
}

// Cada apertura monta un formulario nuevo con el día civil actual de Chile.
export const IngresoJAModal: React.FC<IngresoJAModalProps> = (props) =>
  props.isOpen ? <FormularioIngresoJA {...props} /> : null;

const FormularioIngresoJA: React.FC<IngresoJAModalProps> = ({ isOpen, onClose, onSuccess, ownerContext }) => {
  const [submitting, setSubmitting] = useState(false);

  const { handleSearch: handleSearchMiembros } = useMiembroSearchJA(ownerContext);
  const { error, clearError, handleError } = useErrorHandler();

  const [origenTipo, setOrigenTipo] = useState<'miembro' | 'externo'>('miembro');
  const [miembroId, setMiembroId] = useState('');
  const [nombreExterno, setNombreExterno] = useState('');
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState(() => hoyEnChile());
  const [descripcion, setDescripcion] = useState('');

  const comprobante = useComprobanteFormulario({
    entidad: entidadDelIngreso(ownerContext),
    fechaMovimiento: fecha,
    permiteSubirDespues: true,
    pideMedioPago: true,
    sentidoDinero: 'ingreso',
  });

  const handleMiembroChange = useCallback((value: string) => {
    setMiembroId(value);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!ownerContext) return;

    if (origenTipo === 'miembro' && !miembroId) {
      handleError('Por favor selecciona un miembro');
      return;
    }

    if (origenTipo === 'externo' && !nombreExterno.trim()) {
      handleError('Por favor ingresa el nombre de la persona externa');
      return;
    }

    if (!monto || Number.parseFloat(monto) <= 0) {
      handleError('Por favor ingresa un monto válido');
      return;
    }

    const errorComprobante = comprobante.validar();
    if (errorComprobante) {
      handleError(errorComprobante);
      return;
    }

    try {
      setSubmitting(true);
      const respaldo = await comprobante.resolver();

      await movimientoService.create({
        fecha,
        tipo: 'ingreso',
        monto: Number.parseFloat(monto),
        descripcion: descripcion.trim() || 'Aporte',
        ...buildOwnerPayload(ownerContext),
        origen_tipo: origenTipo,
        origen_id: origenTipo === 'miembro' ? miembroId : undefined,
        nombre_externo: origenTipo === 'externo' ? nombreExterno.trim() : undefined,
        categoria_aporte: origenTipo === 'miembro' ? 'aporte_mensual' : undefined,
        requiere_reembolso: false,
        reembolsado: false,
        anulado: false,
        afecta_balance: true,
        ...respaldo
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'Error al registrar el ingreso. Por favor intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setOrigenTipo('miembro');
    setMiembroId('');
    setNombreExterno('');
    setMonto('');
    setFecha(hoyEnChile());
    setDescripcion('');
    comprobante.reiniciar();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const hasFormChanges = () =>
    origenTipo !== 'miembro' || miembroId !== '' || nombreExterno !== '' || monto !== '' || descripcion !== '' || comprobante.hayCambios;

  const hasUnsavedChanges = submitting || hasFormChanges();
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  if (!isOpen) return null;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <ModalHeader
          title="Registrar Ingreso"
          icon={<DollarSign className="w-6 h-6 text-emerald-600" />}
          onClose={requestClose}
        />
      )}
    >
          <form onSubmit={handleSubmit} className="space-y-6">
            <FormErrorAlert error={error} onClear={clearError} />

            <ComprobanteInput comprobante={comprobante} deshabilitado={submitting} />

            <div>
              <p id="tipo-aportante-label" className="block text-sm font-semibold text-slate-700 mb-3">Tipo de Aportante</p>
              <div aria-labelledby="tipo-aportante-label" className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setOrigenTipo('miembro')}
                  className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${origenTipo === 'miembro' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                >
                  <UsersIcon className="w-5 h-5" />
                  <span className="font-medium">Miembro</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOrigenTipo('externo')}
                  className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${origenTipo === 'externo' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300 text-slate-600'}`}
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Externo</span>
                </button>
              </div>
            </div>

            {origenTipo === 'miembro' ? (
              <ComboboxLazy
                label="Seleccionar Miembro"
                value={miembroId}
                onChange={handleMiembroChange}
                onSearch={handleSearchMiembros}
                placeholder="Buscar miembro por nombre o RUT..."
                emptyMessage="No se encontraron miembros"
                required
                allowClear
              />
            ) : (
              <TextInput
                label="Nombre del Aportante"
                value={nombreExterno}
                onChange={setNombreExterno}
                placeholder="Nombre completo de la persona externa"
                required
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="monto" className="block text-sm font-semibold text-slate-700 mb-2">Monto *</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                  <input
                    type="number"
                    id="monto"
                    value={monto}
                    onChange={(e) => setMonto(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="1"
                    className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="fecha" className="block text-sm font-semibold text-slate-700 mb-2">Fecha *</label>
                <input
                  type="date"
                  id="fecha"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  required
                />
              </div>
            </div>

            <MedioPagoFolio comprobante={comprobante} deshabilitado={submitting} />

            <TextInput
              label="Descripción (opcional)"
              value={descripcion}
              onChange={setDescripcion}
              placeholder="Agrega detalles sobre este ingreso..."
            />

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={requestClose}
                className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitting || comprobante.subiendo}
              >
                {submitting ? 'Guardando...' : 'Registrar Ingreso'}
              </button>
            </div>
          </form>
    </DialogShell>
  );
};
